import React, { useMemo } from 'react';
import { SelectButton } from 'primereact/selectbutton';
import useWarrantyReports, { ReportType } from './hooks/useWarrantyReports';
import WarrantyReportsTable from './components/WarrantyReportsTable';
import WarrantyReportsFilter from './components/WarrantyReportsFilter';
import { useLanguage } from '../../../contexts/LanguageContext';
import PartReplacementDetailsDialog from './components/PartReplacementDetailsDialog';
import GenericFileExportButton from '../../../components/common/GenericFileExportButton';
import { useLazyExportAllClaimsReportsQuery, useLazyExportMostReplacedPartsQuery, useLazyExportDealerChassisCostsQuery } from '../../../api/endpoints/warrantyReports';
import { selectUserRoles } from '../../../features/auth/authSlice';
import { Roles } from '../../../utils/roleEnums';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatDateForApi } from '../../../utils/dateUtils';

const columnsByType = (th) => ({
    [ReportType.AllWarrantyClaims]: [
        { field: 'WarrClaimsNo', header: th('warrantyReports.columns.WarrClaimsNo') },
        { field: 'FullName', header: th('warrantyReports.columns.FullName') },
        { field: 'ChassisNo', header: th('warrantyReports.columns.ChassisNo') },
        { field: 'ClaimDate', header: th('warrantyReports.columns.ClaimDate'), dataType: 'date' },
        { field: 'TotalPrice', header: th('warrantyReports.columns.TotalPrice') },
        { field: 'AcceptedTotalAmount', header: th('warrantyReports.columns.AcceptedTotalAmount') },
        { field: 'Text', header: th('warrantyReports.columns.Text') }
    ],
    [ReportType.MostReplacedParts]: [
        { field: 'ItemCode', header: th('warrantyReports.columns.ItemCode') },
        { field: 'ItemName', header: th('warrantyReports.columns.ItemName') },
        { field: 'TotalQuantity', header: th('warrantyReports.columns.TotalQuantity') }
    ],
    [ReportType.DealerChassisCosts]: [
        { field: 'FullName', header: th('warrantyReports.columns.FullName') },
        { field: 'ChassisNo', header: th('warrantyReports.columns.ChassisNo') },
        { field: 'RequestedTotalAmount', header: th('warrantyReports.columns.RequestedTotalAmount') },
        { field: 'AcceptedTotalAmount', header: th('warrantyReports.columns.AcceptedTotalAmount') },
        { field: 'ItemAcceptedCustomerReplacement', header: th('warrantyReports.columns.ItemAcceptedCustomerReplacement') },
        { field: 'ItemAcceptedKarsanWarrantyReplacement', header: th('warrantyReports.columns.ItemAcceptedKarsanWarrantyReplacement') },
        { field: 'ItemAcceptedServiceWarrantyReplacement', header: th('warrantyReports.columns.ItemAcceptedServiceWarrantyReplacement') },
        { field: 'LaborAcceptedCustomer', header: th('warrantyReports.columns.LaborAcceptedCustomer') },
        { field: 'LaborAcceptedKarsanWarranty', header: th('warrantyReports.columns.LaborAcceptedKarsanWarranty') },
        { field: 'LaborAcceptedServiceWarranty', header: th('warrantyReports.columns.LaborAcceptedServiceWarranty') },
        { field: 'OtherAcceptedCustomer', header: th('warrantyReports.columns.OtherAcceptedCustomer') },
        { field: 'OtherAcceptedKarsanWarranty', header: th('warrantyReports.columns.OtherAcceptedKarsanWarranty') },
        { field: 'OtherAcceptedServiceWarranty', header: th('warrantyReports.columns.OtherAcceptedServiceWarranty') }
    ]
});

const reportTypeOptions = (th) => [
    { label: th('warrantyReports.reportTypes.allClaims'), value: ReportType.AllWarrantyClaims },
    { label: th('warrantyReports.reportTypes.mostReplacedParts'), value: ReportType.MostReplacedParts },
    { label: th('warrantyReports.reportTypes.dealerChassisCosts'), value: ReportType.DealerChassisCosts }
];

export default function WarrantyReportsPage() {
    const { th, getCurrentLanguageId } = useLanguage();
    const navigate = useNavigate();

    const userRoles = useSelector(selectUserRoles);
    const isDealer = Array.isArray(userRoles) && userRoles.includes(Roles.DEALER);
    const { reportType, setReportType, items, totalCount, isFetching, lazyParams, onPage, activeFilters, applyFilters, resetFilters } = useWarrantyReports();

    const handleClaimNavigate = (row) => {
        if (!row?.Id) return;
        navigate(`/warranty-management/warranty-requests/claims/${row.Id}`);
    };

    const renderClaimNo = (row) => {
        if (!row?.WarrClaimsNo) return '';

        return (
            <span
                onClick={() => handleClaimNavigate(row)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleClaimNavigate(row);
                    }
                }}
                style={{
                    color: '#0d6efd',
                    textDecoration: 'underline',
                    cursor: 'pointer'
                }}
            >
                {row.WarrClaimsNo}
            </span>
        );
    };

    const columns = useMemo(() => {
        const base = columnsByType(th)[reportType] || [];

        if (reportType === ReportType.AllWarrantyClaims) {
            return base.map((c) => (c.field === 'WarrClaimsNo' ? { ...c, body: renderClaimNo } : c));
        }

        return base;
    }, [reportType, th]);

    const [detailOpen, setDetailOpen] = React.useState(false);
    const [detailItemCode, setDetailItemCode] = React.useState(null);

    const exportParams = useMemo(() => {
        const baseParams = {
            CompressionType: 0,
            LanguageId: getCurrentLanguageId() || 1
        };

        switch (reportType) {
            case ReportType.AllWarrantyClaims:
                return {
                    ...baseParams,
                    DealerId: activeFilters.dealerId ?? undefined,
                    StartDate: formatDateForApi(activeFilters.startDate) || undefined,
                    EndDate: formatDateForApi(activeFilters.endDate) || undefined,
                    IsDomestic: activeFilters.isDomestic ?? undefined
                };

            case ReportType.MostReplacedParts:
                return {
                    ...baseParams,
                    ItemCode: activeFilters.itemCode ? String(activeFilters.itemCode).trim() : undefined
                };

            case ReportType.DealerChassisCosts:
                return {
                    ...baseParams,
                    DealerId: activeFilters.dealerId ?? undefined,
                    StartDate: formatDateForApi(activeFilters.startDate) || undefined,
                    EndDate: formatDateForApi(activeFilters.endDate) || undefined,
                    IsDomestic: activeFilters.isDomestic ?? undefined
                };

            default:
                return baseParams;
        }
    }, [reportType, activeFilters, getCurrentLanguageId]);

    const getExportHook = () => {
        switch (reportType) {
            case ReportType.AllWarrantyClaims:
                return useLazyExportAllClaimsReportsQuery;
            case ReportType.MostReplacedParts:
                return useLazyExportMostReplacedPartsQuery;
            case ReportType.DealerChassisCosts:
                return useLazyExportDealerChassisCostsQuery;
            default:
                return useLazyExportAllClaimsReportsQuery;
        }
    };

    const exportFileName = useMemo(() => {
        const typeLabel = (() => {
            switch (reportType) {
                case ReportType.AllWarrantyClaims:
                    return th('warrantyReports.reportTypes.allClaims');
                case ReportType.MostReplacedParts:
                    return th('warrantyReports.reportTypes.mostReplacedParts');
                case ReportType.DealerChassisCosts:
                    return th('warrantyReports.reportTypes.dealerChassisCosts');
                default:
                    return 'WarrantyReport';
            }
        })();

        const safeLabel = typeLabel.replace(/\s+/g, '_');
        return `${safeLabel}`;
    }, [reportType, th]);

    const onDetail = (row) => {
        if (!row?.ItemCode) return;
        setDetailItemCode(row.ItemCode);
        setDetailOpen(true);
    };

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('warrantyReports.title')}</h1>
                <div className="flex align-items-center gap-2" style={{ minWidth: 280 }}>
                    <SelectButton
                        value={reportType}
                        options={reportTypeOptions(th)}
                        onChange={(e) => setReportType(e.value)}
                        optionLabel="label"
                        allowEmpty={false}
                        unselectable={false}
                        className="w-full"
                        pt={{
                            button: { className: 'flex-1 text-sm font-semibold' },
                            root: { className: 'w-full' }
                        }}
                    />
                </div>
            </div>

            <WarrantyReportsFilter key={`wr-filter-${reportType}`} onApplyFilters={applyFilters} onResetFilters={resetFilters} reportType={reportType} isDealer={isDealer} />

            <div className="flex justify-content-end mb-3">
                <GenericFileExportButton
                    useExportQuery={getExportHook()}
                    exportParams={exportParams}
                    buttonLabel={th('common.export')}
                    buttonClassName="p-button-outlined"
                    icon="pi pi-file-excel"
                    disabled={isFetching || !totalCount} // veri yoksa butonu devre dışı bırak
                    fileNameExtractor={() => exportFileName}
                    dataExtractor={(result) => result?.fileBase64}
                    contentTypeExtractor={(result) => result?.contentType}
                    exportFormat="base64"
                />
            </div>

            {items && items.length > 0 && (
                <div className="mt-3">
                    <WarrantyReportsTable
                        items={items}
                        totalCount={totalCount}
                        loading={isFetching}
                        lazyParams={lazyParams}
                        onPage={onPage}
                        columns={columns}
                        tableKey={`warranty-reports-${reportType}`}
                        actionColumn={reportType === ReportType.MostReplacedParts ? { onAction: onDetail } : undefined}
                    />
                </div>
            )}

            {detailOpen && <PartReplacementDetailsDialog visible={detailOpen} onHide={() => setDetailOpen(false)} itemCode={detailItemCode} chassisNumber={activeFilters.chassisNumber} isDealer={isDealer} />}
        </div>
    );
}
